import axios from 'axios';
import { API_BASE_URL, API_VERSION, TIMEOUT, CONTENT_TYPE, ENABLE_AES } from '../constants';
import { refreshTokenApi } from '../../api';

// 创建axios实例
const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT
});

// 是否正在刷新token
let isRefreshing = false;
// 重试队列
let retryRequests = [];

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 从localStorage获取token
    const token = localStorage.getItem('token');
    
    // 设置通用请求头
    config.headers['version'] = API_VERSION;
    config.headers['Content-Type'] = CONTENT_TYPE;
    
    // 如果有token则添加到请求头
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }
    
    return config;
  },
  error => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data;
    
    // 如果启用了AES加密，这里可以添加解密逻辑
    if (ENABLE_AES) {
      // 解密逻辑，类似于你抖音小程序中的AesDecrypt
      // const decryptedData = AesDecrypt(res);
      // return JSON.parse(decryptedData);
    }
    
    // 根据后端返回的状态码进行处理
    if (res.rc !== 1) {
      // 处理错误情况
      console.error('API错误:', res.msg || '未知错误');
      
      // 处理token过期的情况 (根据实际后端返回码调整)
      if (res.rc === 401 || res.rc === 403) {
        return handleTokenExpired(response.config);
      }
      
      return Promise.reject(new Error(res.msg || '未知错误'));
    } else {
      return res;
    }
  },
  error => {
    if (error.response) {
      // 处理token过期的情况
      if (error.response.status === 401 || error.response.status === 403) {
        return handleTokenExpired(error.config);
      }
    }
    console.error('响应错误:', error);
    return Promise.reject(error);
  }
);

// 处理token过期
async function handleTokenExpired(config) {
  if (!isRefreshing) {
    isRefreshing = true;
    
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // 没有refreshToken，需要重新登录
        handleLogout();
        return Promise.reject(new Error('登录已过期，请重新登录'));
      }
      
      // 调用刷新token的API
      const res = await refreshTokenApi(refreshToken);
      
      if (res.rc === 1 && res.data) {
        // 保存新的token
        const { accessToken, refreshToken: newRefreshToken, expire } = res.data;
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('expireAt', String(Math.floor(Date.now() / 1000 + expire)));
        
        // 重新发送队列中的请求
        retryRequests.forEach(cb => cb(accessToken));
        retryRequests = [];
        
        // 重试当前请求
        config.headers['Authorization'] = 'Bearer ' + accessToken;
        return service(config);
      } else {
        // 刷新token失败，需要重新登录
        handleLogout();
        return Promise.reject(new Error('登录已过期，请重新登录'));
      }
    } catch (err) {
      // 刷新token出错，需要重新登录
      handleLogout();
      return Promise.reject(new Error('登录已过期，请重新登录'));
    } finally {
      isRefreshing = false;
    }
  } else {
    // 正在刷新token，将请求加入队列
    return new Promise((resolve) => {
      retryRequests.push((token) => {
        config.headers['Authorization'] = 'Bearer ' + token;
        resolve(service(config));
      });
    });
  }
}

// 处理登出
function handleLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('expireAt');
  // 可以在这里添加重定向到登录页的逻辑
  window.location.reload();
}

// 封装GET请求
export function get(url, params = {}) {
  return service({
    url,
    method: 'get',
    params
  });
}

// 封装POST请求
export function post(url, data = {}) {
  return service({
    url,
    method: 'post',
    data
  });
}

// 封装带参数的POST请求
export function postWithParams(url, data = {}, params = {}) {
  return service({
    url,
    method: 'post',
    params,
    data
  });
}

export default service; 